<script setup lang="ts">
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import CopyElement from "@/components/CopyElement.vue";
import { formatDateTime } from '@/services/functions';
import DeliveryPlan from "@/types/DeliveryPlan";
import { computed } from 'vue';
import i18n from '@/lang/i18n';

const emit = defineEmits(['update:modelValue']);

const props = defineProps<{
  modelValue: (DeliveryPlan | getDeliveryPlanVehicleInformation)[]
  originDeliveryPlan: getDeliveryPlanVehicleInformation
  deliveryPlan: DeliveryPlan | getDeliveryPlanVehicleInformation
}>();

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const isOriginDeliveryPlan = computed(() => {
  return props.deliveryPlan.delivery_plan_id === props.originDeliveryPlan.delivery_plan_id;
})

function removeDeliveryPlan(deliveryPlan: DeliveryPlan | getDeliveryPlanVehicleInformation) {
  const index = props.modelValue.findIndex((plan) => plan.delivery_plan_id === deliveryPlan.delivery_plan_id);

  if (index !== -1) {
    modelValue.value.splice(index, 1);
  }
}

function changeFormat(dateTime: Date | string) {
  return typeof dateTime === 'string' ? formatDateTime(new Date(dateTime)) : formatDateTime(dateTime);
}
</script>
<template>
  <v-card
    :class="['w-100', { 'origin-delivery-plan-card': isOriginDeliveryPlan }]"
  >
    <div
      class="w-100 d-flex ml-2"
    >
      <div class="w-75">
        <div class="w-75 d-flex cols-12 ml-2 mt-2">
          <div class="w-75 cols-5">
            <div class="text-label-large font-weight-bold">
              {{ i18n.global.t('pages.dailyReports.combineForm.rightPane.tripId') }}
            </div>
          </div>
          <div class="w-100 d-flex cols-7">
            {{ props.deliveryPlan.delivery_plan_id?.toString().slice(-7) }}
            <CopyElement :copyText="props.deliveryPlan.delivery_plan_id" size="small" />
          </div>
        </div>
        <div class="w-75 d-flex cols-12 ml-2 mt-2">
          <div class="w-75 cols-5">
            <div class="text-label-large font-weight-bold">
              {{ i18n.global.t('pages.dailyReports.combineForm.rightPane.numberPlate') }}
            </div>
          </div>
          <div class="w-100 d-flex cols-7">
            {{ props.deliveryPlan.car_number }}
          </div>
        </div>
        <div class="w-75 d-flex cols-12 ml-2 mt-2">
          <div class="w-75 cols-5">
            <div class="text-label-large font-weight-bold">
              {{ i18n.global.t('pages.dailyReports.combineForm.rightPane.leavingWarehouse') }}
            </div>
          </div>
          <div class="w-100 d-flex cols-7">
            {{ changeFormat(props.deliveryPlan.leaving_warehouse_date_time) }}
          </div>
        </div>
        <div class="w-75 d-flex cols-12 ml-2 my-2">
          <div class="w-75 cols-5">
            <div class="text-label-large font-weight-bold">
              {{ i18n.global.t('pages.dailyReports.combineForm.rightPane.returningWarehouse') }}
            </div>
          </div>
          <div class="w-100 d-flex cols-7">
            {{ changeFormat(props.deliveryPlan.returning_warehouse_date_time) }}
          </div>
        </div>
      </div>
      <div
        class="w-25 d-flex flex-column align-items-end pr-4 my-auto"
      >
        <v-btn
          v-if="!isOriginDeliveryPlan"
          variant="text"
          class="w-25 mx-auto d-flex justify-content-center text-red"
          @click="removeDeliveryPlan(props.deliveryPlan)"
        >
          <span class="my-auto material-symbols-outlined icon-24">
            delete
          </span>
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<style scoped>
.origin-delivery-plan-card {
  border: 2px solid #3388DC !important;
}
</style>
